import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Leaf, ArrowLeft, Target, Package, BarChart3, Lightbulb,
  Lock, Unlock, FileDown, History, ChevronRight, FileText, Download
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Phase Components
import Phase1GoalScope from '../components/lca/Phase1GoalScope';
import Phase2Inventory from '../components/lca/Phase2Inventory';
import Phase3LCIA from '../components/lca/Phase3LCIA';
import Phase4Interpretation from '../components/lca/Phase4Interpretation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const phaseConfig = {
  1: { name: 'Goal & Scope', icon: Target, component: Phase1GoalScope },
  2: { name: 'Inventory (LCI)', icon: Package, component: Phase2Inventory },
  3: { name: 'Impact (LCIA)', icon: BarChart3, component: Phase3LCIA },
  4: { name: 'Interpretation', icon: Lightbulb, component: Phase4Interpretation }
};

export default function ProjectView() {
  const { projectId, phaseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhase, setActivePhase] = useState(parseInt(phaseId) || 1);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  useEffect(() => {
    if (phaseId) {
      setActivePhase(parseInt(phaseId));
    }
  }, [phaseId]);

  const fetchProject = async () => {
    try {
      const response = await api.get(`/projects/${projectId}`);
      setProject(response.data);
      if (!phaseId) {
        setActivePhase(response.data.current_phase);
      }
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handlePhaseChange = (phase) => {
    setActivePhase(phase);
    navigate(`/project/${projectId}/phase/${phase}`);
  };

  const handleExportPDF = async (format) => {
    setExporting(true);
    const formatLabels = {
      'full': 'Full LCA Report',
      'epd': 'EPD Format',
      'cbam': 'CBAM Format'
    };
    
    try {
      toast.info(`Generating ${formatLabels[format]}...`);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/export/pdf/${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name.replace(/\s+/g, '_')}_${format.toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${formatLabels[format]} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${formatLabels[format]}`);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  if (!project) return null;

  const PhaseComponent = phaseConfig[activePhase]?.component;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="project-view-page">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/')}
                data-testid="back-to-dashboard-btn"
                className="text-slate-500 hover:text-sky-700 hover:bg-sky-50"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <img src="/saarv-logo.svg" alt="Saarv" className="h-12 w-auto" />
                <div>
                  <h1 className="text-lg font-bold text-slate-900">{project.name}</h1>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs border-sky-600 text-sky-700">v{project.version}</Badge>
                    <span className="text-xs text-slate-500">
                      Phase {project.current_phase} Active
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={exporting}
                    data-testid="export-btn" 
                    className="border-sky-600 text-sky-700 hover:bg-sky-50"
                  >
                    {exporting ? (
                      <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-sky-600 border-t-transparent" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Export PDF
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white">
                  <DropdownMenuLabel>PDF Export Options</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => handleExportPDF('full')}
                    className="cursor-pointer"
                    data-testid="export-full-btn"
                  >
                    <FileText className="h-4 w-4 mr-2 text-sky-600" />
                    <div>
                      <div className="font-medium">Full LCA Report</div>
                      <div className="text-xs text-slate-500">Complete report with all phases</div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleExportPDF('epd')}
                    className="cursor-pointer"
                    data-testid="export-epd-btn"
                  >
                    <FileText className="h-4 w-4 mr-2 text-emerald-600" />
                    <div>
                      <div className="font-medium">EPD Format</div>
                      <div className="text-xs text-slate-500">ISO 14025 compliant</div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleExportPDF('cbam')}
                    className="cursor-pointer"
                    data-testid="export-cbam-btn"
                  >
                    <FileText className="h-4 w-4 mr-2 text-amber-600" />
                    <div>
                      <div className="font-medium">CBAM Format</div>
                      <div className="text-xs text-slate-500">EU Carbon Border Adjustment</div>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Phase Navigation */}
          <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-2">
            {[1, 2, 3, 4].map((phase) => {
              const config = phaseConfig[phase];
              const PhaseIcon = config.icon;
              const status = project.phase_statuses[String(phase)];
              const isLocked = status === 'locked';
              const isActive = activePhase === phase;
              const isCurrent = project.current_phase === phase;
              const canAccess = phase <= project.current_phase || isLocked;

              return (
                <React.Fragment key={phase}>
                  <button
                    onClick={() => canAccess && handlePhaseChange(phase)}
                    disabled={!canAccess}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      isActive 
                        ? 'bg-sky-600 text-white shadow-lg shadow-sky-600/30' 
                        : isLocked 
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                          : isCurrent 
                            ? 'bg-sky-100 text-sky-700 hover:bg-sky-200' 
                            : 'bg-slate-100 text-slate-400'
                    } ${!canAccess ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`phase-${phase}-nav-btn`}
                  >
                    {isLocked ? <Lock className="h-4 w-4" /> : <PhaseIcon className="h-4 w-4" />}
                    <span className="font-medium text-sm whitespace-nowrap">{config.name}</span>
                  </button>
                  {phase < 4 && (
                    <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={activePhase}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {PhaseComponent && (
              <PhaseComponent 
                project={project} 
                onUpdate={fetchProject}
                isLocked={project.phase_statuses[String(activePhase)] === 'locked'}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
